<template>
  <v-dialog
    v-model="dialogState"
    width="780"
    persistent
  >
    <v-card>
      <v-card-title class="font-weight-light text-uppercase">
        <v-row>
          <v-col
            cols="12"
          >
            <v-icon class="mr-2">
              event
            </v-icon> Update Record
          </v-col>
          <v-col
            cols="12"
            class="py-0 rec-holder"
          >
            <span>Record Holder:</span> <span>{{ recordHolderName }}</span>
            <v-btn
              elevation="0"
              icon
              @click="goToEditParty(localRecord.record_holder_party_id)"
            >
              <v-icon>edit</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-form
          ref="dialogForm"
          v-model="allInputValid"
          action="/"
          method="post"
          @submit="onSubmit"
        >
          <v-row>
            <v-col
              v-if="currentUserAdmin && invoicedEnabled"
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="localRecord.invoiced_customer_id"
                :color="invoicedFieldLocked ? '#ababab' : 'info'"
                :hint="invoicedFieldLocked ? 'Click pencil icon to edit this field' : ''"
                label="Invoiced Customer ID"
                persistent-hint
                filled
                :readonly="invoicedFieldLocked"
              >
                <template #append-outer>
                  <!-- eslint-disable vue/no-v-text-v-html-on-component -->
                  <v-icon
                    :key="`icon-${invoicedFieldLocked}`"
                    :color="invoicedFieldLocked ? null : 'info'"
                    @click="invoicedFieldLocked = !invoicedFieldLocked"
                    v-text="invoicedFieldLocked ? 'edit' : 'check_circle_outline'"
                  />
                  <!-- eslint-enable -->
                  <a
                    href="https://innovationlawlab.org/docs/lawlab-6-faqs/how-do-i-update-a-customers-invoiced-id/"
                    target="_blank"
                  >
                    <v-icon title="Help with Invoiced customer id">help_outline</v-icon></a>
                </template>
              </v-text-field>
            </v-col>
            <v-col
              v-if="currentUser.isAdmin"
              cols="12"
              :sm="currentUserAdmin && invoicedEnabled ? 6 : 12"
            >
              <v-autocomplete
                v-model="localRecord.team_ids"
                multiple
                filled
                dense
                chips
                deletable-chips
                hide-selected
                :items="teamsOptions"
                label="Teams"
                @change="onTeamChange"
              />
            </v-col>
            <v-col
              v-if="currentUser.atleastContributor"
              cols="12"
            >
              <v-autocomplete
                v-model="localRecord.contact_party_id"
                clearable
                append-outer-icon="edit"
                filled
                :items="recordPartiesOptions"
                label="Contact Party"
                :rules="[rules.required]"
                @click:append-outer="goToEditParty(localRecord.contact_party_id)"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="localRecord.other"
                filled
                label="Other"
                :rules="[rules.maxChars(15, localRecord.other)]"
              />
            </v-col>
          </v-row>

          <TmetricFields
            v-if="isTmetricEnabledForCurrentUser"
            @update="updateTmetricAttributes"
          />

          <v-card-actions>
            <v-btn
              text
              color="secondary"
              @click="dialogState = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              type="submit"
              :disabled="submitDisabled"
              :loading="requestLoading"
            >
              Update
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { isObject } from 'lodash'

import { alertHandler } from '@/alertHandler'
import { partyName } from '@/utilities'
import { rules } from '@/utilities/validation-rules'

import { LEGAL_CASE_TYPE_NAMESPACE } from '@/store/legal-case-type'
import { RECORD_NAMESPACE } from '@/store/record'
import { SHARED_NAMESPACE } from '@/store/shared'

import TmetricFields from './Case/TmetricFields.vue'

const UNPERMITTED_PARAMS = ['created_at', 'updated_at', 'lock_version', 'teams',
  'legal_cases', 'record_holder_party', 'contact_party', 'invoicedCustomerId']

export default {
  name: 'RecordDialog',

  components: {
    TmetricFields,
  },

  props: {
    invoicedEnabled: {
      type: Boolean,
      default: false,
    },

    openLocally: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      invoicedFieldLocked: true,
      isObject,
      localRecord: {},
      recordScheme: {
        alphabetHint: null,
      },

      requestLoading: false,
      rules: {
        ...rules,
        recordPublicId: null,
      },

      valid: true,
    }
  },

  computed: {
    allInputValid: {
      get () {
        return this.valid
      },

      set (newVal) {
        this.valid = newVal
      },
    },

    currentUserAdmin () {
      return this.currentUser.user_type === 'admin'
    },

    dialogState: {
      get () {
        // in party edit view, record dialogs need to open without using the state's dialogOpenStates
        if (this.openLocally) return true

        return this.editRecordDialogOpen
      },

      set (newValue) {
        if (newValue === this.dialogState) return

        this.setEditRecordDialog(newValue)
      },
    },

    recordHolderName () {
      return this.recordHolderParty
        ? partyName(this.recordHolderParty)
        : ''
    },

    recordSchemeHasAlphabet () {
      let result = false
      if (this.recordSchemeIsSetUp) {
        if (this.recordSchemeSettings.all_user_values) result = this.recordSchemeSettings.all_user_values.scheme.includes('alphabet')
        else if (this.recordSchemeSettings.value) {
          const value = JSON.parse(this.recordSchemeSettings.value)
          result = value.scheme.includes('alphabet')
        }
      }

      return result
    },

    recordSchemeIsSetUp () {
      return this.recordSchemeSettings &&
        Object.keys(this.recordSchemeSettings).length > 0
    },

    recordSchemeSettings () {
      return this.features?.record_public_id_scheme
    },

    submitDisabled () {
      return !this.allInputValid || this.requestLoading
    },

    ...mapGetters(RECORD_NAMESPACE, [
      'currentRecord',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
      'isTmetricEnabledForCurrentUser',
    ]),

    ...mapGetters([
      'currentUser',
      'editRecordDialogOpen',
      'recordHolderParty',
      'recordPartiesOptions',
      'teams',
      'teamsOptions',
    ]),
  },

  created () {
    this.getTmetricEnabledForCurrentUser()
    this.getFeatures()

    this.localRecord = { ...this.currentRecord }
    if (this.localRecord.teams.length > 0) this.localRecord.team_ids = this.localRecord.teams.map(t => t.id)

    if (Object.keys(this.recordPartiesOptions).length === 0) {
      this.getRecordPartiesModel(this.currentRecord.id).
        then(_ => (this.requestLoading = false)).
        catch(error => {
          this.requestLoading = false
          this.displayAlert('error', undefined, error)
        })
    }

    if (this.teamsOptions.length === 0) {
      this.getTeamsModel().
        then(_ => (this.requestLoading = false)).
        catch(error => {
          this.requestLoading = false
          this.displayAlert('error', undefined, error)
        })
    }
  },

  methods: {
    goToEditParty (partyId) {
      this.$router.push(`/party/${partyId}/edit`)
    },

    onSubmit (e) {
      e.preventDefault()
      this.requestLoading = true

      // need to strip hyphens out of masked a_number
      if (this.localRecord.a_number) this.localRecord.a_number = this.localRecord.a_number.replace(/[-]/g, '')

      const recordToSave = Object.assign({}, this.localRecord)
      UNPERMITTED_PARAMS.forEach(p => delete recordToSave[p])

      this.updateRecord({
        recordData: recordToSave,
        recordId: this.currentRecord.id,
      }).
        then(_ => {
          this.dialogState = false
          this.displayAlert('success', 'Successfully updated record')
        }).
        catch(error => {
          this.requestLoading = false
          this.displayAlert('error', undefined, error)
        })
    },

    onTeamChange (selectedIds) {
      this.getSelectedLegalCaseTypes({ filter_name: 'team_ids', filter_value: selectedIds })
    },

    updateTmetricAttributes (data) {
      this.localRecord.budget_size = data.budgetSize
      this.localRecord.tmetric_client_id = data.tmetricClientId
    },

    ...mapActions(LEGAL_CASE_TYPE_NAMESPACE, [
      'getSelectedLegalCaseTypes',
    ]),

    ...mapActions(RECORD_NAMESPACE, [
      'updateRecord',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getFeatures',
      'getTmetricEnabledForCurrentUser',
    ]),

    ...mapActions([
      'getRecordPartiesModel',
      'getTeamsModel',
    ]),

    ...mapMutations([
      'setEditRecordDialog',
    ]),
  },
}
</script>
